import { FlexLayout, H4 } from '@deity/falcon-ui';
import type {
  IEndpointAuthentication,
  IEndpointBasicAuthentication,
  IEndpointOAuth2Authentication,
  IEndpointTokenAuthentication
} from '@deity-io/di-data-flows';
import { Input, Select } from '@deity-io/portal-ui';
import type { FC } from 'react';
import { useState } from 'react';

interface IAuthorizationProps {
  isEditable: boolean;
  authentication?: IEndpointAuthentication;
  onChange: (key: string[], value: IEndpointAuthentication | null, set?: boolean) => void;
}

const Authentication: FC<IAuthorizationProps> = ({ isEditable, authentication, onChange }) => {
  const [authType, setAuthType] = useState<'token' | 'none' | 'oauth2' | 'basic'>(authentication?.type || 'none');

  return (
    <FlexLayout p="xxl" flexDirection="column">
      <H4 fontWeight="semiBold" fontSize="xl">
        Authentication & Authorization
      </H4>
      <Select
        label="Type"
        sx={{ marginY: 1 }}
        onChange={(val: any) => setAuthType(val?.value)}
        options={['none', 'token', 'oauth2', 'basic'].map(auth => ({
          value: auth,
          label: auth.toUpperCase()
        }))}
        placeholder="select type"
        value={{ label: (authType || 'none').toUpperCase(), value: authType || 'none' }}
        disabled={!isEditable}
        disableClearable
      />
      {authType === 'token' && (
        <>
          <Input
            label="Authorization token"
            type="text"
            name="authorization-token"
            id="authorization-token"
            value={(authentication as IEndpointTokenAuthentication)?.authToken}
            onChange={e =>
              onChange(['authentication'], {
                type: authType,
                clientToken: (authentication as IEndpointTokenAuthentication)?.clientToken || '',
                authToken: e.target.value
              })
            }
            placeholder="{variables.TOKEN_NAME}"
            disabled={!isEditable}
            sx={{ marginY: 2 }}
            autoComplete="off"
          />
          <Input
            label="Client token"
            type="text"
            name="client-token"
            id="client-token"
            value={(authentication as IEndpointTokenAuthentication)?.clientToken}
            onChange={e =>
              onChange(['authentication'], {
                type: authType,
                authToken: (authentication as IEndpointTokenAuthentication)?.authToken || '',
                clientToken: e.target.value
              })
            }
            placeholder="{variables.TOKEN_NAME}"
            disabled={!isEditable}
            sx={{ marginY: 2 }}
            autoComplete="off"
          />
        </>
      )}
      {authType === 'oauth2' && (
        <>
          <Input
            label="Token URL"
            type="text"
            name="token-url"
            id="token-url"
            value={(authentication as IEndpointOAuth2Authentication)?.tokenUrl}
            onChange={e =>
              onChange(['authentication'], {
                type: authType,
                tokenUrl: e.target.value,
                grantType: (authentication as IEndpointOAuth2Authentication)?.grantType || 'client_credentials',
                clientId: (authentication as IEndpointOAuth2Authentication)?.clientId || '',
                clientSecret: (authentication as IEndpointOAuth2Authentication)?.clientSecret || ''
              })
            }
            placeholder="Enter token URL"
            disabled={!isEditable}
            sx={{ marginY: 2 }}
            autoComplete="off"
          />
          <Input
            label="Client ID"
            type="text"
            name="client-id"
            id="client-id"
            value={(authentication as IEndpointOAuth2Authentication)?.clientId}
            onChange={e =>
              onChange(['authentication'], {
                type: authType,
                clientId: e.target.value,
                grantType: (authentication as IEndpointOAuth2Authentication)?.grantType || 'client_credentials',
                tokenUrl: (authentication as IEndpointOAuth2Authentication)?.tokenUrl || '',
                clientSecret: (authentication as IEndpointOAuth2Authentication)?.clientSecret || ''
              })
            }
            placeholder="Enter client ID"
            disabled={!isEditable}
            sx={{ marginY: 2 }}
            autoComplete="off"
          />
          <Input
            label="Client Secret"
            type="password"
            name="client-secret"
            id="client-secret"
            value={(authentication as IEndpointOAuth2Authentication)?.clientSecret}
            onChange={e =>
              onChange(['authentication'], {
                type: authType,
                clientSecret: e.target.value,
                grantType: (authentication as IEndpointOAuth2Authentication)?.grantType || 'client_credentials',
                tokenUrl: (authentication as IEndpointOAuth2Authentication)?.tokenUrl || '',
                clientId: (authentication as IEndpointOAuth2Authentication)?.clientId || ''
              })
            }
            placeholder="Enter client secret"
            disabled={!isEditable}
            sx={{ marginY: 2 }}
            autoComplete="off"
          />
        </>
      )}
      {authType === 'basic' && (
        <>
          <Input
            label="Username"
            type="text"
            name="username"
            id="username"
            value={(authentication as IEndpointBasicAuthentication)?.username}
            onChange={e =>
              onChange(['authentication'], {
                type: authType,
                username: e.target.value,
                password: (authentication as IEndpointBasicAuthentication)?.password || ''
              })
            }
            placeholder="Enter username"
            disabled={!isEditable}
            sx={{ marginY: 2 }}
            autoComplete="off"
          />
          <Input
            label="Password"
            type="password"
            name="password"
            id="password"
            value={(authentication as IEndpointBasicAuthentication)?.password}
            onChange={e =>
              onChange(['authentication'], {
                type: authType,
                username: (authentication as IEndpointBasicAuthentication)?.username || '',
                password: e.target.value
              })
            }
            placeholder="Enter password"
            disabled={!isEditable}
            sx={{ marginY: 2 }}
            autoComplete="off"
          />
        </>
      )}
    </FlexLayout>
  );
};

export default Authentication;
